'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BotyLogo } from '@boty/ui';
import type { WelcomeSectionProps } from './types';

/**
 * Welcome section with headline and value proposition.
 *
 * Supports two modes:
 * - Default: Large headline with full subtitle (desktop)
 * - Compact: Smaller headline with brief subtitle (mobile)
 *
 * Epic 6 Note: This component is designed to be portable to the marketing site.
 * The compact prop allows for A/B testing different headline sizes.
 */
export function WelcomeSection({ compact = false }: WelcomeSectionProps) {
  const t = useTranslations('creator.landing.welcome');

  if (compact) {
    return (
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <BotyLogo size="small" />
        </Box>
        <Typography
          variant="h5"
          component="h1"
          fontWeight={700}
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          {t('title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 400, mx: 'auto' }}
        >
          {t('subtitleShort')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <BotyLogo size="medium" />
      </Box>
      <Typography
        variant="h3"
        component="h1"
        fontWeight={700}
        gutterBottom
        sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
      >
        {t('title')}
      </Typography>
      <Typography
        variant="h6"
        color="text.secondary"
        sx={{
          maxWidth: 500,
          mx: 'auto',
          fontWeight: 400,
          fontSize: { xs: '1rem', md: '1.125rem' },
        }}
      >
        {t('subtitle')}
      </Typography>
    </Box>
  );
}
